import React, { useMemo, useState } from 'react';
import { Badge, Card, CardBody } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ConfirmDialog, ErrorResponse, Spinner } from '~components';

import { GocardlessConnection } from '~direct-debits/models';

import { useDisconnectConnectionMutation, useGetConnectionsQuery } from '~direct-debits/actions/connections';
import { useLazyInitiateOAuthQuery } from '~direct-debits/actions/oauth';

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type InitiatingAction = 'login' | 'signup' | null;

const ConnectionManager: React.FC = () => {
  const { data: connections, isLoading, isFetching, isError, error } = useGetConnectionsQuery();
  const [initiateOAuth] = useLazyInitiateOAuthQuery();
  const [disconnectConnection, { isLoading: isDisconnecting }] = useDisconnectConnectionMutation();
  const { showToast } = useToast();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  // Track which button was clicked so only that button shows the "Redirecting..." label —
  // using the shared RTK Query isFetching flag would flip both buttons simultaneously.
  const [initiatingAction, setInitiatingAction] = useState<InitiatingAction>(null);

  // The API's exchange_token deactivates any prior active row transactionally before
  // creating a new one, so in practice there should only ever be a single active row.
  // Pick defensively though: if duplicates somehow exist, take the most recently
  // connected one so the UI shows/disconnects a deterministic choice.
  const activeConnection: GocardlessConnection | null = useMemo(() => {
    const active = connections?.filter((connection) => connection.status === 'active') ?? [];
    if (active.length === 0) return null;
    if (active.length === 1) return active[0];
    return [...active].sort((first, second) => {
      const firstTs = new Date(first.connected_at ?? first.created_at).getTime();
      const secondTs = new Date(second.connected_at ?? second.created_at).getTime();
      return secondTs - firstTs;
    })[0];
  }, [connections]);

  const handleConnect = async (initialView: 'login' | 'signup') => {
    setInitiatingAction(initialView);
    try {
      const result = await initiateOAuth({ initial_view: initialView }).unwrap();
      window.location.href = result.url;
    } catch (err) {
      showToast('danger', getErrorMessage(err));
      setInitiatingAction(null);
    }
  };

  const handleDisconnectConfirm = async (confirmed: boolean) => {
    setShowDisconnectConfirm(false);
    if (!confirmed || !activeConnection) return;

    try {
      await disconnectConnection(activeConnection.id).unwrap();
      showToast('success', 'GoCardless account disconnected');
    } catch (err) {
      showToast('danger', getErrorMessage(err));
    }
  };

  if (isLoading) {
    return <Spinner loading className="p-5" />;
  }

  if (isError) {
    return <ErrorResponse message={getErrorMessage(error)} />;
  }

  // Background refetch indicator — rendered into the heading so the user can see that a
  // refresh is in flight without losing the already-rendered content.
  const refreshIndicator = isFetching ? (
    <Spinner loading className="ms-2" type="beat" size={8} data-cy="dd-connection-refreshing" />
  ) : null;

  if (activeConnection) {
    return (
      <div data-cy="dd-connection-status">
        <Card className="mt-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h5 className="mb-2">
                  <Badge color="success" className="me-2">
                    Connected
                  </Badge>
                  GoCardless Account
                  {refreshIndicator}
                </h5>
                <div className="text-muted small">
                  {activeConnection.organisation_id && (
                    <div>
                      <strong>Organisation:</strong> {activeConnection.organisation_id}
                    </div>
                  )}
                  <div>
                    <strong>Environment:</strong> {activeConnection.environment}
                  </div>
                  <div>
                    <strong>Connected:</strong> {formatDate(activeConnection.connected_at)}
                  </div>
                </div>
              </div>
              <Button
                color="danger"
                size="sm"
                onClick={() => setShowDisconnectConfirm(true)}
                disabled={isDisconnecting}
                data-cy="dd-disconnect-btn"
              >
                {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
              </Button>
            </div>
          </CardBody>
        </Card>
        <ConfirmDialog
          isOpen={showDisconnectConfirm}
          onClose={handleDisconnectConfirm}
          messages={[
            'This will revoke the GoCardless connection and remove the stored access token.',
            'You will need to re-authorize to use GoCardless features again.',
          ]}
        />
      </div>
    );
  }

  return (
    <div data-cy="dd-connection-status">
      <Card className="mt-3">
        <CardBody className="text-center py-5">
          <h5 className="mb-3">
            Connect your GoCardless account
            {refreshIndicator}
          </h5>
          <p className="text-muted mb-4">
            Link your GoCardless account to manage direct debit payments through TurnPoint.
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              color="primary"
              onClick={() => handleConnect('signup')}
              disabled={initiatingAction !== null}
              data-cy="dd-connect-create-btn"
            >
              {initiatingAction === 'signup' ? 'Redirecting...' : 'Create New Account'}
            </Button>
            <Button
              color="success"
              onClick={() => handleConnect('login')}
              disabled={initiatingAction !== null}
              data-cy="dd-connect-link-btn"
            >
              {initiatingAction === 'login' ? 'Redirecting...' : 'Link Existing Account'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ConnectionManager;
